import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { AlertTriangle, CheckCircle, Sparkles, X, ChevronDown } from 'lucide-react';
import { useAppSelector } from '@/stores';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { selectCurrentThreadId } from '@/stores/selectors/chat-selectors';
import { selectUserPreferences } from '@/stores/selectors/user-selectors';
import { useStreamContext } from '@/providers/stream-provider';
import { appConfig } from '@/config/app-config';
import { STORAGE_KEYS } from '@/config/storage-config';

// ── Team configs ──────────────────────────────────────────────────────────────

const DEMO_TEAMS = [
  {
    id: 'laundry',
    label: 'ACME Hotel – Laundry Service',
    description: 'Lighter demo: fewer resources, faster setup — great for quick walkthroughs.',
    endpoint: 'create-laundry-demo-team',
    teamName: 'ACME Hotel – Laundry Service (demo)',
    modalItems: [
      'Team: ACME Hotel – Laundry Service',
      'Users, schedules & escalation policies',
      'Services & business services',
    ],
  },
  {
    id: 'guest-experience',
    label: 'ACME Hotel – Guest Experience',
    description: 'Full demo: users, schedules, EPs, services, EO & workflows with intentional issues.',
    endpoint: 'create-demo-team',
    teamName: 'ACME Hotel – Guest Experience (demo)',
    modalItems: [
      'Team: ACME Hotel – Guest Experience',
      'Users, schedules & escalation policies',
      'Services, event orchestrations & workflows',
      'Business services & dependencies',
    ],
  },
] as const;

type DemoTeamId = typeof DEMO_TEAMS[number]['id'];

// ── Warning Modal ──────────────────────────────────────────────────────────────

interface DemoTeamModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  items: string[];
}

const DemoTeamModal: React.FC<DemoTeamModalProps> = ({ isOpen, onClose, onConfirm, items }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#ffffff', borderRadius: '12px', maxWidth: '480px', width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #e2e8f0',
          backgroundColor: '#f7fafc', borderTopLeftRadius: '12px', borderTopRightRadius: '12px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <AlertTriangle size={18} color="#D69E2E" />
            <span style={{ fontSize: '16px', fontWeight: 600, color: '#1a202c' }}>
              Create demo team in PagerDuty?
            </span>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096', padding: '4px', display: 'flex', alignItems: 'center', borderRadius: '4px' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#e2e8f0')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <p style={{ margin: 0, fontSize: '14px', color: '#4a5568' }}>
            This will create real resources in your live PagerDuty instance:
          </p>
          <ul style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {items.map((item) => (
              <li key={item} style={{ fontSize: '13px', color: '#718096' }}>{item}</li>
            ))}
          </ul>
          <div style={{ backgroundColor: '#fffbeb', border: '1px solid #f6e05e', borderRadius: '8px', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#744210' }}>
              After creation a summary will appear in the current chat so you can select and analyze the demo team right away.
            </p>
            <p style={{ margin: 0, fontSize: '11px', color: '#92400e', fontStyle: 'italic' }}>
              Requires an <strong>Enterprise Incident Management (EIM)</strong> plan with <strong>AIOps</strong> enabled.
            </p>
          </div>
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button onClick={onClose} style={{ fontSize: '14px', fontWeight: 500, padding: '6px 16px', borderRadius: '6px', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', color: '#4a5568', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f7fafc')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}>
            Cancel
          </button>
          <button onClick={onConfirm} style={{ fontSize: '14px', fontWeight: 500, padding: '6px 16px', borderRadius: '6px', border: 'none', backgroundColor: '#06AC38', color: '#ffffff', cursor: 'pointer' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#059030')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#06AC38')}>
            Yes, create it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ── Section Component ──────────────────────────────────────────────────────────

export const DemoTeamSection: React.FC = () => {
  const { submit } = useStreamContext();
  const selectedAgent = useAppSelector(selectSelectedAgent);
  const currentThreadId = useAppSelector(selectCurrentThreadId);
  const preferences = useAppSelector(selectUserPreferences);
  const agentId = selectedAgent?.id || 'optimize-clean-up-agent';

  const [selectedId, setSelectedId] = useState<DemoTeamId>('laundry');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (preferences?.show_demo_section === false) return null;

  const selected = DEMO_TEAMS.find((t) => t.id === selectedId)!;

  const handleConfirm = async () => {
    setIsModalOpen(false);
    setIsLoading(true);
    setError(null);

    const tid = currentThreadId || sessionStorage.getItem('THREAD_ID');
    if (!tid) { setError('No active thread — start a conversation first'); setIsLoading(false); return; }

    const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';

    try {
      const res = await fetch(
        `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${tid}/${selected.endpoint}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
      );
      const data = await res.json();
      if (res.ok && data.success) {
        setIsDone(true);
        await submit({
          messages: [{
            type: 'human' as const,
            content: `Demo team '${selected.teamName}' was just created. Please select this team and proceed directly to the analysis without asking the user to choose a team.`,
          }],
        });
      } else {
        setError(data.message || 'Creation failed');
      }
    } catch {
      setError('Network error — please try again');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div>
        <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 600, color: '#718096', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          Demo
        </p>
        <div style={{ border: '1px solid #f6d860', borderRadius: '8px', backgroundColor: '#fffbeb', padding: '10px 12px' }}>
          {isDone ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CheckCircle size={14} color="#d97706" />
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400e' }}>Demo team ready!</span>
              </div>
              <span style={{ fontSize: '12px', color: '#b45309' }}>See the summary in chat.</span>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Sparkles size={13} color="#d97706" />
                <span style={{ fontSize: '12px', fontWeight: 600, color: '#92400e' }}>Create demo team</span>
              </div>

              {/* Team selector */}
              <div style={{ position: 'relative' }}>
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value as DemoTeamId)}
                  style={{
                    width: '100%', fontSize: '12px', fontWeight: 500,
                    padding: '5px 24px 5px 8px', borderRadius: '6px',
                    border: '1px solid #f59e0b', backgroundColor: '#fef9c3',
                    color: '#92400e', cursor: 'pointer', appearance: 'none',
                    outline: 'none',
                  }}
                >
                  {DEMO_TEAMS.map((t) => (
                    <option key={t.id} value={t.id}>{t.label}</option>
                  ))}
                </select>
                <ChevronDown size={12} color="#92400e" style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
              </div>

              <p style={{ margin: 0, fontSize: '11px', color: '#b45309' }}>{selected.description}</p>

              <button
                disabled={isLoading}
                onClick={() => setIsModalOpen(true)}
                style={{
                  fontSize: '12px', fontWeight: 500, padding: '5px 10px', borderRadius: '6px',
                  border: '1px solid #f59e0b',
                  backgroundColor: isLoading ? '#fef3c7' : '#fef9c3',
                  color: isLoading ? '#b45309' : '#92400e',
                  cursor: isLoading ? 'not-allowed' : 'pointer', alignSelf: 'flex-start',
                }}
                onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#fde68a'; }}
                onMouseLeave={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = '#fef9c3'; }}
              >
                {isLoading ? 'Setting up demo…' : 'Create demo team'}
              </button>
              {error && <p style={{ margin: 0, fontSize: '11px', color: '#e53e3e' }}>{error}</p>}
            </div>
          )}
        </div>
      </div>

      <DemoTeamModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={handleConfirm}
        items={selected.modalItems}
      />
    </>
  );
};
